import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useAppSelector } from '../../redux/store';
import api from '../../Services/api';
import Globals from '../../Globals.json';
import { notify, notifyError } from '../notify/notify-component';
import * as S from './styled';

interface Props {
  show: boolean;
  onHide: () => void;
}

interface FlatCategory {
  id: number;
  name: string;
  level: number;
  childrenIds: number[];
}

type Selection = Record<number, Set<number>>;

const PRINTERS_COUNT = 20;
const printerIndexes = Array.from({ length: PRINTERS_COUNT }, (_, i) => i);

const emptySelection = (): Selection =>
  printerIndexes.reduce((acc, i) => {
    acc[i] = new Set<number>();
    return acc;
  }, {} as Selection);

const cloneSelection = (source: Selection): Selection =>
  printerIndexes.reduce((acc, i) => {
    acc[i] = new Set(source[i]);
    return acc;
  }, {} as Selection);

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Achata a árvore, guarda os ids das descendentes de cada categoria
// e preenche as impressoras já configuradas (xd_printers).
function flatten(
  nodes: any[],
  level: number,
  list: FlatCategory[],
  printers: Selection
): number[] {
  const ids: number[] = [];
  nodes.forEach((node) => {
    const entry: FlatCategory = {
      id: node.id,
      name: node.name,
      level,
      childrenIds: [],
    };
    list.push(entry);
    ids.push(node.id);

    const bits = (node.xd_printers || '').split(',');
    printerIndexes.forEach((i) => {
      if (bits[i] === '1') printers[i].add(node.id);
    });

    entry.childrenIds = flatten(
      node.relationships?.children || [],
      level + 1,
      list,
      printers
    );
    ids.push(...entry.childrenIds);
  });
  return ids;
}

export function ModalAjusteImpressoras({ show, onHide }: Props) {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);

  const [categories, setCategories] = useState<FlatCategory[]>([]);
  const [original, setOriginal] = useState<Selection>(emptySelection);
  const [selection, setSelection] = useState<Selection>(emptySelection);
  const [activePrinter, setActivePrinter] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [applyToProducts, setApplyToProducts] = useState<boolean>(true);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const response = await api.get(`${Globals.api.treeCategories}`, {
        params: {
          page: 1,
          paginate: false,
          store_id: storeMe?.storeActive?.id,
        },
      });

      const printers = emptySelection();
      const list: FlatCategory[] = [];
      flatten(response.data.data || [], 0, list, printers);

      setCategories(list);
      setOriginal(printers);
      setSelection(cloneSelection(printers));
    } catch (error) {
      notifyError('Erro ao carregar categorias');
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoadingData(false);
    }
  }, [storeMe?.storeActive?.id]);

  useEffect(() => {
    if (show) {
      setActivePrinter(0);
      setSearch('');
      loadData();
    }
  }, [show, loadData]);

  // Diferença por impressora em relação ao que já estava salvo.
  const changes = useMemo(
    () =>
      printerIndexes
        .map((i) => ({
          printer: i + 1,
          attach_category_ids: Array.from(selection[i]).filter(
            (id) => !original[i].has(id)
          ),
          detach_category_ids: Array.from(original[i]).filter(
            (id) => !selection[i].has(id)
          ),
        }))
        .filter(
          (c) => c.attach_category_ids.length || c.detach_category_ids.length
        ),
    [original, selection]
  );

  const changedPrinters = useMemo(
    () => new Set(changes.map((c) => c.printer - 1)),
    [changes]
  );

  const visibleCategories = useMemo(() => {
    const term = normalize(search.trim());
    if (!term) return categories;
    return categories.filter((c) => normalize(c.name).includes(term));
  }, [categories, search]);

  const current = selection[activePrinter];

  const updateCurrent = (update: (next: Set<number>) => void) => {
    setSelection((prev) => {
      const next = new Set(prev[activePrinter]);
      update(next);
      return { ...prev, [activePrinter]: next };
    });
  };

  // Marcar/desmarcar uma categoria aplica o mesmo estado às subcategorias.
  const handleToggle = (category: FlatCategory) => {
    const checked = current.has(category.id);
    updateCurrent((next) => {
      [category.id, ...category.childrenIds].forEach((id) =>
        checked ? next.delete(id) : next.add(id)
      );
    });
  };

  const allVisibleChecked =
    visibleCategories.length > 0 &&
    visibleCategories.every((c) => current.has(c.id));

  const handleToggleVisible = () => {
    updateCurrent((next) =>
      visibleCategories.forEach((c) =>
        allVisibleChecked ? next.delete(c.id) : next.add(c.id)
      )
    );
  };

  const handleSave = async () => {
    if (!storeMe?.storeActive?.id) {
      notifyError('Loja não identificada');
      return;
    }

    // Envia o estado completo (não só a diferença): assim o salvar também
    // corrige categorias/produtos copiados de outra loja ou dessincronizados.
    // O backend só grava o que realmente estiver diferente.
    const fullState = printerIndexes.map((i) => ({
      printer: i + 1,
      attach_category_ids: categories
        .filter((c) => selection[i].has(c.id))
        .map((c) => c.id),
      detach_category_ids: categories
        .filter((c) => !selection[i].has(c.id))
        .map((c) => c.id),
    }));

    setSaving(true);
    try {
      await api.post(Globals.api.categoriesBulkPrinters, {
        store_id: storeMe.storeActive.id,
        apply_to_products: applyToProducts,
        changes: fullState,
      });
      notify('Impressoras das categorias atualizadas com sucesso!');
      onHide();
    } catch (error) {
      notifyError('Erro ao atualizar impressoras das categorias');
      console.error('Erro ao atualizar impressoras:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <S.ModalWrapper show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Ajuste de Impressoras por Categoria</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loadingData ? (
          <div className="text-center p-4">
            <div className="spinner-border" role="status" />
          </div>
        ) : (
          <S.Layout>
            <S.PrinterList>
              {printerIndexes.map((i) => (
                <S.PrinterItem
                  key={i}
                  type="button"
                  $active={i === activePrinter}
                  onClick={() => setActivePrinter(i)}
                >
                  <span>Local de impressão {i + 1}</span>
                  <S.Badge $changed={changedPrinters.has(i)}>
                    {selection[i].size}
                  </S.Badge>
                </S.PrinterItem>
              ))}
            </S.PrinterList>

            <S.Panel>
              <S.Toolbar>
                <Form.Control
                  size="sm"
                  placeholder="Buscar categoria..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={handleToggleVisible}
                  disabled={visibleCategories.length === 0}
                >
                  {allVisibleChecked ? 'Desmarcar todas' : 'Marcar todas'}
                </Button>
              </S.Toolbar>

              <S.Muted className="mb-2">
                Categorias que imprimem no{' '}
                <b>Local de impressão {activePrinter + 1}</b>
              </S.Muted>

              <S.Tree>
                {visibleCategories.length === 0 ? (
                  <S.Muted className="d-block p-3">
                    Nenhuma categoria encontrada.
                  </S.Muted>
                ) : (
                  visibleCategories.map((category) => {
                    const checked = current.has(category.id);
                    return (
                      <S.Row
                        key={category.id}
                        $checked={checked}
                        $level={search ? 0 : category.level}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggle(category)}
                        />
                        {category.name.toUpperCase()}
                      </S.Row>
                    );
                  })
                )}
              </S.Tree>
            </S.Panel>
          </S.Layout>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div>
          <Form.Check
            type="switch"
            id="apply-printers-to-products"
            label="Refletir também nos produtos das categorias"
            checked={applyToProducts}
            onChange={(e) => setApplyToProducts(e.target.checked)}
          />
          <S.Muted>
            {changes.length === 0
              ? 'Nenhuma alteração pendente — salvar reaplica a configuração atual'
              : `${changes.length} impressora(s) com alterações pendentes`}
          </S.Muted>
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || loadingData || categories.length === 0}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </Modal.Footer>
    </S.ModalWrapper>
  );
}
